import React, {
    createContext,
    PropsWithChildren,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useApolloClient, useLazyQuery, useMutation, useQuery } from '@apollo/client';
import {
    AUTOCOMPLETE_QUERY,
    AutoCompleteQuery,
    AutoCompleteQueryVariables,
    CAMPAIGN_RESULTS_QUERY,
    OVERALL_CAMPAIGN_RESULTS_QUERY,
    CAMPAIGN_VIS_MAP_QUERY,
} from 'data/ForecastingResults';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { ReactComponent as LoadingState } from 'svg/loader.svg';
import classNames from 'layout/classNames';
import Select, { MultiValue } from 'react-select';
import AsyncSelect from 'react-select/async';
import { CreativeType, ForecastingStatus, SearchType } from '../data/__generated__/globalTypes';
import Base64 from 'base64-js';
import { CampaignVisOverallResultsQuery } from '../data/__generated__/CampaignVisOverallResultsQuery';
import { CampaignVisResultsQuery } from '../data/__generated__/CampaignVisResultsQuery';
import { LatLng, LatLngBounds, map } from 'leaflet';
import { useMap } from 'react-leaflet';
import { ForecastingState } from '../components/ForecastingContextProvider';

export type VisualizationType =
    | 'numberOfScreens'
    | 'potentialSpend'
    | 'averageCpm'
    | 'numberOfImpressions'
    | 'numberOfAudienceImpressions';

export type CampaignVisualisationSSP =
    | 'placeexchange'
    | 'vistar'
    | 'broadsign'
    | 'viooh'
    | 'onetechssp1'
    | 'dax';

export type CampaignVisualisationsSettingsValue = {
    ssp: CampaignVisualisationSSP;
    visualizationType: VisualizationType;
    publisherFilter: string[] | null;
    metroFilter: {label: string, value: string}[] | null;
    venueTypeFilter: ({label: string;value: string;})[] | null;
    campaignId: string | null;
};

export const CampaignVisualisationSettingsSessionKey = 'CAMPAIGN_VISUALISATION_SETTINGS';

export const DefaultCampaignVisualisationSettingsValue = {
    ssp: 'broadsign',
    visualizationType: 'numberOfImpressions',
    publisherFilter: null,
    metroFilter: null,
    venueTypeFilter: null,
    campaignId: null
} as CampaignVisualisationsSettingsValue;

function useInitialCampaignVisualisationSettings(): CampaignVisualisationsSettingsValue {
    const { settings } = useParams()
    if (settings != null) {
        try {
            let decoder = new TextDecoder();
            let CampaignVisualisationSettingsFromParams = JSON.parse(decoder.decode(Base64.toByteArray(settings)));
            if (typeof CampaignVisualisationSettingsFromParams === 'object') {
                return CampaignVisualisationSettingsFromParams;
            }
        }
        catch (e) {
            console.log('Exception in decoding CampaignVisualisationSettingsFromParams. Will use DefaultCampaignVisualisationSettingsValue');
        }
    }
    return DefaultCampaignVisualisationSettingsValue;
}

export const CampaignVisualisationSettingsContext = createContext({
    campaignVisualisationSettings: DefaultCampaignVisualisationSettingsValue,
    setCampaignVisualisationSettings: (nextSettings: CampaignVisualisationsSettingsValue) => {},
});

export const useCampaignVisualisationSettings = () => useContext(CampaignVisualisationSettingsContext);

export function CampaignVisualisationSettingsProvider({
                                                children
                                            }: PropsWithChildren<{}>) {
    const initialCampaignVisualisationSettings = useInitialCampaignVisualisationSettings();
    const [campaignVisualisationSettings, setCampaignVisualisationSettings] = useState(initialCampaignVisualisationSettings);

    const setAndCacheCampaignVisualisationSettings = useCallback((
        nextSettings: CampaignVisualisationsSettingsValue
    ) => {
        setCampaignVisualisationSettings(nextSettings);
    }, [setCampaignVisualisationSettings]);
    return (
        <CampaignVisualisationSettingsContext.Provider
            value={{
                campaignVisualisationSettings,
                setCampaignVisualisationSettings: setAndCacheCampaignVisualisationSettings,
            }}
        >
            {children}
        </CampaignVisualisationSettingsContext.Provider>
    );
}

export default function CampaignVisSettings(Props: {setFlyDest: (flyDest: LatLngBounds | null) => void}) {
    const { campaignVisualisationSettings, setCampaignVisualisationSettings } =
        useCampaignVisualisationSettings();

    const { loading : loadingOne, error : errorOne, data : campaignVisData } = useQuery(CAMPAIGN_RESULTS_QUERY, {variables : {campaignId: campaignVisualisationSettings.campaignId}});
    const { loading : loadingTwo, error : errorTwo, data : overallCampaignVisData} = useQuery(OVERALL_CAMPAIGN_RESULTS_QUERY, { variables: { campaignId: campaignVisualisationSettings.campaignId} });
    const campaignSummary =
        campaignVisData?.campaignVisualisationResults.at(0) ?? ['', ''];
    const overallCampaignSummary =
        overallCampaignVisData?.overallCampaignVisualisationResults.at(0) ?? ['', ''];
    const setSSP = (ssp: CampaignVisualisationSSP) => {
        setCampaignVisualisationSettings({
            ...campaignVisualisationSettings,
            ssp,
            publisherFilter: null,
        });
    };
    const map = useMap();

    const minLat = overallCampaignSummary?.minLat;
    const minLon = overallCampaignSummary?.minLon;
    const maxLat = overallCampaignSummary?.maxLat;
    const maxLon = overallCampaignSummary?.maxLon;


    useEffect(() => {
        if (minLat == null || minLon == null || maxLat == null || maxLon == null) {
            return;
        }

        const boundary = new LatLngBounds(
            new LatLng(minLat, minLon),
            new LatLng(maxLat, maxLon)
        );
        const zoom = map.getBoundsZoom(boundary);
        const center = boundary.getCenter()
        Props.setFlyDest(boundary)
        map.flyTo(center, zoom, {easeLinearity: 1, animate: true});
    }, [minLat,
        minLon,
        maxLat,
        maxLon]);

    const setCampaignId = (updatedCampaignId: string) => {
        setCampaignVisualisationSettings({...campaignVisualisationSettings, campaignId: updatedCampaignId,});
    }

    const [textboxCampaignId, setTextboxCampaignId] = useState('');
    const handleChange = (event: { target: { value: any; }; }) => {
        setTextboxCampaignId(event.target.value);
    }

    return (
        <>
            <div className='leaflet-top leaflet-right'>
                <div className='bg-white rounded-md py-4 px-4 m-4 shadow-sm pointer-events-auto'>
                    <div className='w-48'>
                    <span className='block text-sm text-gray-700 font-bold underline'>
                        Campaign Visualisation summary
                    </span>
                        <div className='mt-2 space-y-1'>
                        <span className='block text-sm font-medium text-gray-700'>
                            Campaign ID: {campaignSummary.campaignId}
                        </span>
                        </div>
                        <div className='mt-2 space-y-1'>
                        <span className='block text-sm font-medium text-gray-700'>
                            Log Entry Time: {campaignSummary.logEntryTime}
                        </span>
                        </div>
                        <div className='mt-2 space-y-1'>
                        <span className='block text-sm font-medium text-gray-700'>
                            Total Campaign Impressions: {overallCampaignSummary.impressions}
                        </span>
                        </div>
                    </div>
                </div>
            </div>
            <div className='leaflet-top leaflet-left bg-white rounded-md py-4 px-4 m-4 shadow-sm pointer-events-auto'>
                <div className='w-fit'>
                    <span className='block text-sm font-semibold text-gray-600'>
                        Campaign Visualisation settings
                    </span>
                </div>
                <div className='max-h-96 overflow-scroll'>
                    <div className='mt-2 w-80'>
                        <label
                            htmlFor='campaignID'
                            className='block text-sm font-medium text-gray-700'
                        >
                            Campaign ID*
                        </label>
                        <input
                            key='campaignIdInput'
                            onChange={handleChange}
                            type={'text'}
                            id='campaignID'
                            name='campaignID'
                            autoComplete='campaignID-name'
                            placeholder={'Enter Campaign ID(s)'}
                            className={classNames(
                                'mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm text-sm focus:outline-none focus:ring-gray-500 focus:border-gray-500'
                            )}
                        >
                        </input>
                    </div>
                </div>
                <div className='w-80 h-10 mt-4 flex'>
                    <button
                        className={classNames(
                            'flex-auto bg-blue-500 text-white font-bold py-2 px-4 rounded hover:bg-blue-700'
                        )}
                        onClick={() => setCampaignId(textboxCampaignId)}
                    >
                        Run campaign visualisation
                    </button>
                </div>
            </div>
        </>
    );
}
